<template>
  <v-container>
    <h2 class="headline">{{ $t('Draft') }}</h2>
    <v-btn
      block
      large
      color="primary"
      :to="{ name: 'assetwise-broadcast-executive-create' }"
      replace
    >
      <v-icon>add</v-icon>
      {{ $t('Add new broadcast') }}
    </v-btn>
    <section class="pa-3">
      <List :title="$t('Draft')" :loading="draftedBroadcast.isLoading">
        <Item
          v-for="item in draftedBroadcast.items"
          :key="item.id"
          :name="item.name"
          :datetime="item.datetime"
          :author="item.author"
          :deleting="item.isDeleting"
          :duplicating="item.isDuplicating"
          @edit="
            $router.replace({
              name: 'assetwise-broadcast-executive-edit',
              params: {
                id: item.id,
                data: item.originalData
              }
            })
          "
          @duplicate="duplicateBroadcast(item)"
          @delete="deleteBroadcast(item)"
        />
      </List>
      <v-pagination
        v-if="totalDraftedPages > 1"
        v-model="draftedBroadcast.pagination.page"
        :length="totalDraftedPages"
        class="mt-3"
        @input="fetchDraftedBroadcasts($event)"
      />
    </section>
    <section class="pa-3">
      <List :title="$t('Broadcasted')" :loading="sentBroadcast.isLoading">
        <Item
          v-for="item in sentBroadcast.items"
          :key="item.id"
          :name="item.name"
          :datetime="item.datetime"
          :author="item.author"
          :scheduled-datetime="item.scheduledDatetime"
          @view="
            $router.replace({
              name: 'assetwise-broadcast-executive-view',
              params: {
                id: item.id,
                data: item.originalData
              }
            })
          "
        />
      </List>
      <v-pagination
        v-if="totalSentPages > 1"
        v-model="sentBroadcast.pagination.page"
        :length="totalSentPages"
        class="mt-3"
        @input="fetchSentBroadcasts($event)"
      />
    </section>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { getBroadcasts, deleteBroadcast, createBroadcast } from '@/api/assetwise'
import List from './List.vue'
import Item from './Item.vue'

export default {
  components: { List, Item },
  data() {
    return {
      draftedBroadcast: {
        items: [],
        pagination: null,
        isLoading: false
      },
      sentBroadcast: {
        items: [],
        pagination: null,
        isLoading: false
      }
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    totalDraftedPages() {
      const { totalItems = 0, rowsPerPage = 10 } = this.draftedBroadcast.pagination || {}
      return Math.ceil(totalItems / rowsPerPage)
    },
    totalSentPages() {
      const { totalItems = 0, rowsPerPage = 10 } = this.sentBroadcast.pagination || {}
      return Math.ceil(totalItems / rowsPerPage)
    }
  },
  created() {
    this.fetchDraftedBroadcasts()
    this.fetchSentBroadcasts()
  },
  methods: {
    async fetchDraftedBroadcasts(page = 1) {
      try {
        this.draftedBroadcast.isLoading = true

        const { rowsPerPage = 10 } = this.draftedBroadcast.pagination || {}

        const { data: items, total: totalItems } = await getBroadcasts({
          page_id: this.selectedBot.pageid,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
          status: 'draft'
        }).then((response) => response.data)

        this.draftedBroadcast.items = items.map((item) => ({
          id: item.id,
          name: item.name,
          datetime: new Date(item.updated_timestamp),
          author: item.create_by,
          isDeleting: false,
          isDuplicating: false,
          originalData: item
        }))
        this.draftedBroadcast.pagination = {
          ...this.draftedBroadcast.pagination,
          rowsPerPage,
          page,
          totalItems
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.draftedBroadcast.isLoading = false
      }
    },
    async fetchSentBroadcasts(page = 1) {
      try {
        this.sentBroadcast.isLoading = true

        const { rowsPerPage = 10 } = this.sentBroadcast.pagination || {}

        const { data: items, total: totalItems } = await getBroadcasts({
          page_id: this.selectedBot.pageid,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
          status: 'sent'
        }).then((response) => response.data)

        this.sentBroadcast.items = items.map((item) => ({
          id: item.id,
          name: item.name,
          datetime: new Date(item.updated_timestamp),
          author: item.create_by,
          scheduledDatetime: item.schedule_timestamp ? new Date(item.schedule_timestamp) : null,
          originalData: item
        }))
        this.sentBroadcast.pagination = {
          ...this.sentBroadcast.pagination,
          rowsPerPage,
          page,
          totalItems
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.sentBroadcast.isLoading = false
      }
    },
    async duplicateBroadcast(item) {
      const { value: isConfirmed } = await this.$swal({
        type: 'question',
        title: this.$t('Do you want to copy {name}?', { name: item.name }),
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel')
      })

      if (!isConfirmed) return

      try {
        item.isDuplicating = true

        const { originalData: data } = item

        await createBroadcast({
          page_id: data.page_id,
          name: data.name,
          target: data.target,
          message_type: data.message_type,
          messages: data.messages,
          ...(data.target === 'custom' && {
            user_ids: data.user_ids,
            team_ids: data.team_ids
          }),
          schedule_timestamp: data.schedule_timestamp,
          create_by: data.create_by,
          status: data.status
        })

        this.fetchDraftedBroadcasts(this.draftedBroadcast.pagination.page)
      } catch (error) {
        item.isDuplicating = false
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      }
    },
    async deleteBroadcast(item) {
      const { value: isConfirmed } = await this.$swal({
        type: 'question',
        title: this.$t('Do you want to delete {name}?', { name: item.name }),
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel')
      })

      if (!isConfirmed) return

      try {
        item.isDeleting = true

        await deleteBroadcast({
          page_id: this.selectedBot.pageid,
          id: item.id
        })

        this.fetchDraftedBroadcasts(this.draftedBroadcast.pagination.page)
      } catch (error) {
        item.isDeleting = false
        this.$swal(this.$t('Something went wrong'), undefined, 'error')
        console.error(error)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/template/AssetWise/ExecutiveBroadcast/index.vue